<template>
  <div :class="prefixCls">
    <div v-if="job" :class="`${prefixCls}-info-block`">
      <span :class="`${prefixCls}-info-block-title`">
        {{ job.title || '岗位名称' }}
      </span>
      <span :class="`${prefixCls}-info-block-info`">
        {{ job.nameTitle || '职位负责人' }}：{{ job.name || '未知' }}
      </span>
    </div>

    <div :class="`${prefixCls}-toolbar`">
      <div :class="`${prefixCls}-toolbar-description`">
        {{ description }}
      </div>
      <a-input
        :class="`${prefixCls}-toolbar-input`"
        placeholder="输入姓名/手机号"
        @input="onInput"
      ></a-input>
    </div>
    <a-row v-if="listData.length" :class="`${prefixCls}-selection`">
      <a-col :span="6">
        <div style="display: flex; align-items: center; white-space: nowrap">
          <a-checkbox
            :model-value="checkedAll"
            :indeterminate="indeterminate"
            @change="handleChangeAll"
            >全选</a-checkbox
          >
          （已选{{ selectedIds.length }}/{{ maxSelectableCount }}）<!--
          <a-tooltip v-if="max" :content="`最多可以选择${maxSelectableCount}个人`">
            <icon-info-circle style="cursor: pointer" /> </a-tooltip
          >
          -->
        </div>
      </a-col>
      <a-col :span="18" :class="`${prefixCls}-selection-area`">
        <a-space align="center" wrap>
          <template v-for="tag in selected">
            <a-tag
              v-if="tag"
              :key="tag.id"
              closable
              color="#e4e9f2"
              style="color: #34363f"
              @close="remove(tag.id)"
            >
              {{ tag.name }}
            </a-tag>
          </template>
        </a-space>
      </a-col>
    </a-row>
    <a-list
      v-if="list.length || loading"
      :class="[`${prefixCls}-list`, { [`${prefixCls}-list-loading`]: loading }]"
      :bordered="false"
      :split="false"
      :max-height="400"
      :loading="loading"
    >
      <a-checkbox-group
        v-model="selectedIds"
        :max="maxSelectableCount"
        @change="handleChange"
      >
        <a-list-item v-for="item in list" :key="item.id">
          <a-checkbox :value="item.id">
            <a-list-item-meta :title="item.name" :description="item.mobile">
              <template #avatar>
                <a-space>
                  <a-avatar v-if="item.avatar" :size="40">
                    <img alt="avatar" :src="item.avatar" />
                  </a-avatar>
                  <IconDefaultAvatarDarkColor
                    v-else-if="defaultAvatarType === 'dark'"
                    :size="40"
                  />
                  <IconDefaultAvatarPrismLightColor
                    v-else-if="defaultAvatarType === 'prism-light'"
                    :size="40"
                  />
                  <IconDefaultAvatarLightColor v-else :size="40" />
                </a-space>
              </template>
            </a-list-item-meta>
          </a-checkbox>
        </a-list-item>
      </a-checkbox-group>
    </a-list>
    <a-empty v-else-if="!loading">
      <template #image>
        <icon-empty-content-color :size="140" />
      </template>
      内容为空
    </a-empty>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, type PropType, watch } from 'vue';
import {
  IconDefaultAvatarPrismLightColor,
  IconDefaultAvatarDarkColor,
  IconDefaultAvatarLightColor,
  IconEmptyContentColor,
  // IconInfoCircle,
} from '../icon';
import { getPrefixCls } from '../_utils/global-config';
import type { IUser, IJob } from './types';

export default defineComponent({
  name: 'AddSomeone',
  components: {
    IconDefaultAvatarPrismLightColor,
    IconDefaultAvatarDarkColor,
    IconDefaultAvatarLightColor,
    IconEmptyContentColor,
    // IconInfoCircle,
  },
  props: {
    /**
     * @zh 列表数据
     * @en list data
     */
    listData: {
      type: Array as PropType<IUser[]>,
      default: () => [],
    },
    /**
     * @zh 职位相关文案
     * @en text of job
     */
    job: {
      type: Object as PropType<IJob>,
      default: () => ({
        id: -1,
        title: '岗位名称',
        nameTitle: '职位负责人',
        name: '某某某',
      }),
    },
    /**
     * @zh 默认选中的项
     * @en default selected items
     */
    defaultSelectedIds: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
    /**
     * @zh 描述文案
     * @en text of description
     */
    description: {
      type: String as PropType<string>,
      default: '职位协作人可共同进行职位管理和候选人管理',
    },
    /**
     * @zh 是否为加载中状态
     * @en list is loading status or not
     */
    loading: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    /**
     * @zh 列表默认头像类型
     * @en default avatar type of list
     */
    defaultAvatarType: {
      type: String as PropType<'dark' | 'light' | 'prism-light'>,
      default: 'prism-light',
    },
    /**
     * @zh 支持最多选中的数量
     * @en supports the maximum number of selections
     */
    max: {
      type: Number as PropType<number>,
    },
    /**
     * @zh 自动判断数据长度和最多选中的数量
     * @en automatically determine data length and maximum selected quantity
     */
    autoMaxLength: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
  },
  setup(props, { expose }) {
    const prefixCls = getPrefixCls('add-someone');
    const selectedIds = ref<number[]>(props.defaultSelectedIds);
    const list = ref<IUser[]>(props.listData);
    const checkedAll = ref(false);
    const indeterminate = ref(false);
    const onInput = (value: string) => {
      const _value = value.trim();
      if (!_value) {
        list.value = props.listData;
        return;
      }
      const isTelephone = /^1[3-9]\d{9}$/.test(value);
      list.value = props.listData.filter((e) => {
        if (isTelephone) {
          return e.mobile?.startsWith(value);
        }
        return e.name?.includes(value) || e.mobile?.includes(value);
      });
    };

    const remove = (id: number) => {
      selectedIds.value = selectedIds.value.filter((e) => e !== id);
    };

    const selected = computed(() => {
      return selectedIds.value.map((e) =>
        props.listData.find((item) => item.id === e)
      ) as IUser[];
    });

    const maxSelectableCount = computed(() => {
      if (!props.autoMaxLength && props.max) {
        return props.max;
      }
      if (!props.max || props.listData.length <= props.max) {
        return props.listData.length;
      }
      return props.max;
    });

    const handleChangeAll = (value: any) => {
      indeterminate.value = false;
      if (value) {
        checkedAll.value = true;
        selectedIds.value = props.listData.map((e) => e.id);
      } else {
        checkedAll.value = false;
        selectedIds.value = [];
      }
    };

    const handleChange = (values: number[]) => {
      if (values.length === maxSelectableCount.value) {
        checkedAll.value = true;
        indeterminate.value = false;
      } else if (values.length === 0) {
        checkedAll.value = false;
        indeterminate.value = false;
      } else {
        checkedAll.value = false;
        indeterminate.value = true;
      }
    };

    watch(
      () => props.listData,
      (data) => {
        list.value = data;
      }
    );

    expose({
      getSelectedIds: () => selectedIds.value,
      getSelected: () => selected.value,
    });

    return {
      prefixCls,
      onInput,
      list,
      selectedIds,
      selected,
      maxSelectableCount,
      remove,
      checkedAll,
      indeterminate,
      handleChange,
      handleChangeAll,
    };
  },
});
</script>
